// Entitlement state for the signed-in user (COL-352, "browse free, pay to
// create"). Non-operators (staff, owners, tenants) are entitled by definition
// — the paywall only ever concerns operator-portal users.
//
// refetchOnWindowFocus picks up webhook-driven status flips when the operator
// returns from checkout; the billing page polls harder on its own.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useOperator } from "@/hooks/useOperator";
import { isEntitledToCreate, type EntitlementStatus } from "@/lib/entitlement";
import type { SaasSubscription } from "@/types/billing";

export function useOperatorEntitlement(): {
  status: EntitlementStatus;
  subscription: Pick<
    SaasSubscription,
    "status" | "plan" | "amount_cents" | "current_period_end" | "started_at"
  > | null;
  /** True when create actions should run; false opens the paywall. */
  isEntitled: boolean;
  isLoading: boolean;
  refetch: () => void;
} {
  const { isOperatorPortal, rolesReady } = useAuth();
  const { operator, isLoading: operatorLoading } = useOperator();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["operator-entitlement", operator?.id],
    enabled: rolesReady && isOperatorPortal && !!operator,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("saas_subscriptions")
        .select("status, plan, amount_cents, current_period_end, started_at")
        .eq("operator_id", operator!.id)
        .order("updated_at", { ascending: false })
        .limit(1);
      if (error) throw error;
      return (rows?.[0] ?? null) as {
        status: SaasSubscription["status"];
        plan: string;
        amount_cents: number;
        current_period_end: string | null;
        started_at: string | null;
      } | null;
    },
  });

  // Staff/owners/tenants: never gated. Operator: gated by the subscription.
  if (!isOperatorPortal) {
    return {
      status: "none",
      subscription: null,
      isEntitled: true,
      isLoading: false,
      refetch: () => {},
    };
  }

  const status: EntitlementStatus = data?.status ?? "none";
  return {
    status,
    subscription: data ?? null,
    isEntitled: isEntitledToCreate({
      isPlatform: operator?.is_platform ?? false,
      status,
    }),
    isLoading: operatorLoading || isLoading,
    refetch,
  };
}
